import React, {useState} from 'react';
import {Col, Container, Row} from 'react-bootstrap';
import SearchInput from '../../../uiToolkit/Inputs/SearchInput';
import {microreelsLibraryText} from '../microreelsLibraryText';
import '../../MicrolessonsLibrary/view/styles.css';
import {Microlesson} from '../../../data/models/Microlessons/Microlesson';
import MicrolessonsLibraryCard from '../../MicrolessonsLibrary/components/MicrolessonLibraryCard';
import TablePagination from '../../../uiToolkit/TablePagination';
import {ITEMS_PER_PAGE} from '../container/MicroreelsLibraryContainer';
import Button from '../../../uiToolkit/Buttons/Button';
import MicroreelsEmptyState from '../../../uiToolkit/EmptyStates/MicroreelsEmptyState';

interface Props {
  search: string;
  setSearch: (search: string) => void;
  onCancel: () => void;
  reels: Microlesson[];
  currentPage: number;
  pages: number;
  onPageChange: (page: number) => void;
  onCopyReels: (microreelIds: string[]) => void;
  isLoading: boolean;
}

const MicroreelsLibraryUI = React.memo(
  ({
    search,
    setSearch,
    onCancel,
    reels,
    currentPage,
    pages,
    onPageChange,
    onCopyReels,
    isLoading,
  }: Props) => {
    // The copy endpoint takes a list, so several reels can go in one call.
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    const onToggleReel = (microreel: Microlesson) => {
      setSelectedIds(current =>
        current.includes(microreel.id)
          ? current.filter(id => id !== microreel.id)
          : [...current, microreel.id],
      );
    };

    const onConfirmSelection = () => {
      if (selectedIds.length) {
        onCopyReels(selectedIds);
      }
    };

    return (
      <div className="MicrolessonsModal">
        <Container fluid>
          <Row>
            <Col xl={{offset: 3, span: 6}} lg={{offset: 2, span: 8}}>
              <div className="MicrolessonsModalContainer">
                <div className="Header">
                  <h1>{microreelsLibraryText.title}</h1>
                  <button className="CloseButton" onClick={onCancel}>
                    <span className="icon-x-close"></span>
                  </button>
                </div>
                <div className="SearchContainer">
                  <SearchInput value={search} onChange={setSearch} />
                </div>

                <Container className="LessonsContainer">
                  <Row>
                    {reels.length ? (
                      reels.map(reel => (
                        <Col xs={4} key={reel.id} className="d-flex">
                          <MicrolessonsLibraryCard
                            lesson={reel}
                            onSelectLesson={onToggleReel}
                            selected={selectedIds.includes(reel.id)}
                          />
                        </Col>
                      ))
                    ) : (
                      <Col xs={12}>
                        <MicroreelsEmptyState
                          title={microreelsLibraryText.emptyTitle}
                          subtitle={microreelsLibraryText.emptySubtitle}
                        />
                      </Col>
                    )}
                  </Row>
                </Container>
                {reels.length ? (
                  <Container>
                    <Row>
                      <Col xs={12}>
                        <TablePagination
                          currentPage={currentPage}
                          rowCount={pages * ITEMS_PER_PAGE}
                          rowsPerPage={ITEMS_PER_PAGE}
                          onChangePage={onPageChange}
                        />
                      </Col>
                    </Row>
                  </Container>
                ) : null}
                {reels.length ? (
                  <div className="d-flex flex-row justify-content-center mt-3">
                    <Button
                      uiType="action"
                      title={`${microreelsLibraryText.select}${
                        selectedIds.length > 1 ? ` (${selectedIds.length})` : ''
                      }`}
                      disabled={!selectedIds.length}
                      onClick={onConfirmSelection}
                      isLoading={isLoading}
                    />
                  </div>
                ) : null}
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  },
);

export default MicroreelsLibraryUI;
